import { useState } from 'react'
import { Link } from 'react-router-dom'
import { companiesApi } from '../api/companies'
import type { QboReference } from '../api/types'
import { Alert } from '../components/Alert'
import { useCompanies } from '../companies/useCompanies'
import { EmptyState, ErrorState, LoadingState } from '../components/States'
import { useAsync } from '../hooks/useAsync'

/* Ported from "batchbooks_item_settings_standalone".

   None of these defaults can be saved: TenantSettings has no fields for item
   type, COGS/income/asset account, inventory as-of date, account type, account
   detail type or entity type. The banner says so and Save is disabled.

   What is real is the account pickers — GET /companies/{id}/references?type=
   Account returns the live chart of accounts, so the dropdowns are populated
   from QuickBooks rather than invented. When the contract grows the fields,
   this page needs a settings call and the banner removed, nothing else. */

const ITEM_TYPES = ['Inventory', 'Non-inventory', 'Service', 'Bundle']
const ACCOUNT_TYPES = [
  'Bank', 'Accounts Receivable', 'Other Current Asset', 'Fixed Asset',
  'Accounts Payable', 'Credit Card', 'Other Current Liability', 'Long Term Liability',
  'Equity', 'Income', 'Cost of Goods Sold', 'Expense', 'Other Income', 'Other Expense',
]
const ENTITY_TYPES = ['Customer', 'Vendor', 'Employee']

function AccountSelect({
  id,
  label,
  accounts,
  value,
  onChange,
}: {
  id: string
  label: string
  accounts: QboReference[]
  value: string
  onChange: (next: string) => void
}) {
  return (
    <div className="bb-field">
      <label className="bb-flabel" htmlFor={id}>
        {label}
      </label>
      <select className="bb-select" id={id} value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">Select…</option>
        {accounts.map((account) => (
          <option value={account.id} key={account.id}>
            {account.name}
          </option>
        ))}
      </select>
    </div>
  )
}

export function ItemSettingsPage() {
  const { current, loading: companiesLoading } = useCompanies()
  const companyId = current?.id ?? null

  const accounts = useAsync<QboReference[]>(
    () => (companyId ? companiesApi.references(companyId, 'Account') : Promise.resolve([])),
    [companyId],
  )

  const [itemType, setItemType] = useState('')
  const [cogs, setCogs] = useState('')
  const [income, setIncome] = useState('')
  const [asset, setAsset] = useState('')
  const [asOf, setAsOf] = useState('')
  const [accountType, setAccountType] = useState(ACCOUNT_TYPES[0])
  const [detailType, setDetailType] = useState('')
  const [entityType, setEntityType] = useState(ENTITY_TYPES[0])

  if (companiesLoading) return <LoadingState label="Loading your companies…" />

  const list = accounts.data ?? []

  return (
    <div className="bb bb-page">
      <h1>Settings</h1>
      <div className="bb-tabs">
        <Link className="bb-tab" to="/app/settings">
          Preferences
        </Link>
        <span className="bb-tab bb-tab-on">Item Settings</span>
      </div>

      <h2 className="bb-sec-h">Item Settings</h2>
      <p className="bb-map-help" style={{ marginBottom: 20 }}>
        Configure default settings for missing item creation and miscellaneous mappings.
      </p>

      <Alert tone="info" title="These defaults cannot be saved yet">
        The settings contract has no fields for any of them, so nothing on this page persists. The
        account pickers below are real — they list the chart of accounts from your connected
        QuickBooks company.
      </Alert>

      {!current ? (
        <EmptyState
          icon="!"
          title="No QuickBooks company connected"
          body="Connect a company to load its chart of accounts into these pickers."
        />
      ) : accounts.error ? (
        <ErrorState error={accounts.error} onRetry={accounts.reload} />
      ) : (
        <>
          <div className="bb-sec" style={{ marginTop: 24 }}>
            <h2 className="bb-sec-h">Default Settings for Missing Item Creation</h2>
            <div className="bb-grid-f">
              <div className="bb-field">
                <label className="bb-flabel" htmlFor="itemType">
                  Item Type
                </label>
                <select
                  className="bb-select"
                  id="itemType"
                  value={itemType}
                  onChange={(e) => setItemType(e.target.value)}
                >
                  <option value="">Select…</option>
                  {ITEM_TYPES.map((type) => (
                    <option value={type} key={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>

              <AccountSelect
                id="cogs"
                label="COGS Account/Expense"
                accounts={list}
                value={cogs}
                onChange={setCogs}
              />
              <AccountSelect
                id="income"
                label="Income Account"
                accounts={list}
                value={income}
                onChange={setIncome}
              />
              <AccountSelect
                id="asset"
                label="Asset Account"
                accounts={list}
                value={asset}
                onChange={setAsset}
              />

              <div className="bb-field">
                <label className="bb-flabel" htmlFor="asOf">
                  Inventory As of Date
                </label>
                <input
                  className="bb-input"
                  id="asOf"
                  type="date"
                  value={asOf}
                  onChange={(e) => setAsOf(e.target.value)}
                />
              </div>
            </div>
          </div>

          <div className="bb-sec">
            <h2 className="bb-sec-h">Miscellaneous Default Settings</h2>
            <div className="bb-grid-f">
              <div className="bb-field">
                <label className="bb-flabel" htmlFor="accountType">
                  Account Type
                </label>
                <select
                  className="bb-select"
                  id="accountType"
                  value={accountType}
                  onChange={(e) => setAccountType(e.target.value)}
                >
                  {ACCOUNT_TYPES.map((type) => (
                    <option value={type} key={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>

              <div className="bb-field">
                <label className="bb-flabel" htmlFor="detailType">
                  Account Detail Type
                </label>
                <select
                  className="bb-select"
                  id="detailType"
                  value={detailType}
                  onChange={(e) => setDetailType(e.target.value)}
                >
                  {/* QuickBooks derives detail types from the account type; the
                      contract exposes no list, so this stays free of invented
                      options until it does. */}
                  <option value="">Not available yet</option>
                </select>
              </div>

              <div className="bb-field">
                <label className="bb-flabel" htmlFor="entityType">
                  Entity Type
                </label>
                <select
                  className="bb-select"
                  id="entityType"
                  value={entityType}
                  onChange={(e) => setEntityType(e.target.value)}
                >
                  {ENTITY_TYPES.map((type) => (
                    <option value={type} key={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </>
      )}

      <div className="bb-wizard-foot">
        <span className="bb-map-help">
          {accounts.loading ? 'Loading your chart of accounts…' : null}
        </span>
        <span className="bb-wizard-foot-r">
          <button className="bb-btn bb-btn-quiet" type="button" disabled>
            Cancel
          </button>
          <button className="bb-btn bb-btn-green" type="button" disabled>
            Save
          </button>
        </span>
      </div>
    </div>
  )
}
